import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditDossierV2 = () => {
  const { id } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const response = await axios.get(`${API}/dossiers/${id}`);
        setDossier(response.data);
      } catch (error) {
        toast.error("Error al cargar dossier");
        navigate("/dossiers");
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const donnees = JSON.parse(JSON.stringify(dossier.donnees));
      if (donnees.geolocalisation) {
        donnees.geolocalisation.huso = String(donnees.geolocalisation.huso || "");
        donnees.geolocalisation.utm_x = parseFloat(donnees.geolocalisation.utm_x) || 0;
        donnees.geolocalisation.utm_y = parseFloat(donnees.geolocalisation.utm_y) || 0;
      }
      if (donnees.batiment) {
        donnees.batiment.superficie_habitable = parseFloat(donnees.batiment.superficie_habitable) || 0;
        donnees.batiment.annee_construction = parseInt(donnees.batiment.annee_construction) || 0;
      }
      if (donnees.techniques) {
        for (const k of ["ui", "uf", "superficie_partition", "surface_murs", "surface_vitres", "total_envolvente", "pourcentage_isole"]) {
          if (k in donnees.techniques) donnees.techniques[k] = parseFloat(donnees.techniques[k]) || 0;
        }
      }
      await axios.put(`${API}/dossiers/${id}`, {
        donnees,
        notes: dossier.notes,
        date_installation: dossier.date_installation
      });
      toast.success("Dossier actualizado");
      navigate(`/dossier/${id}`);
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const updateIdentite = (field, value) => {
    setDossier({
      ...dossier,
      donnees: {
        ...dossier.donnees,
        identite: { ...dossier.donnees.identite, [field]: value }
      }
    });
  };

  const updateBatiment = (field, value) => {
    setDossier({
      ...dossier,
      donnees: {
        ...dossier.donnees,
        batiment: { ...dossier.donnees.batiment, [field]: value }
      }
    });
  };

  const updateTechniques = (field, value) => {
    const newTechniques = { ...dossier.donnees.techniques, [field]: value };
    
    // Auto-recalculate totals when lists or isolee change
    const opaques = newTechniques.surfaces_opaques_list || [];
    const vitres = newTechniques.surfaces_vitrees_list || [];
    const isolee = newTechniques.superficie_partition || 0;
    
    const s_opaques = opaques.length > 0 ? Math.round(opaques.reduce((a, b) => a + b, 0) * 100) / 100 : newTechniques.surface_murs || 0;
    const s_vitres = vitres.length > 0 ? Math.round(vitres.reduce((a, b) => a + b, 0) * 100) / 100 : newTechniques.surface_vitres || 0;
    const total = Math.round((s_opaques + s_vitres) * 100) / 100;
    const pct = total > 0 ? Math.round((isolee * 100 / total) * 100) / 100 : 0;
    
    newTechniques.surface_murs = s_opaques;
    newTechniques.surface_vitres = s_vitres;
    newTechniques.total_envolvente = total;
    newTechniques.pourcentage_isole = pct;
    
    setDossier({
      ...dossier,
      donnees: {
        ...dossier.donnees,
        techniques: newTechniques
      }
    });
  };

  const addSurfaceItem = (listField) => {
    const currentList = dossier?.donnees?.techniques?.[listField] || [];
    updateTechniques(listField, [...currentList, 0]);
  };

  const removeSurfaceItem = (listField, index) => {
    const currentList = dossier?.donnees?.techniques?.[listField] || [];
    updateTechniques(listField, currentList.filter((_, i) => i !== index));
  };

  const updateSurfaceItem = (listField, index, value) => {
    const currentList = [...(dossier?.donnees?.techniques?.[listField] || [])];
    currentList[index] = parseFloat(value) || 0;
    updateTechniques(listField, currentList);
  };

  const updateGeolocalisation = (field, value) => {
    setDossier({
      ...dossier,
      donnees: {
        ...dossier.donnees,
        geolocalisation: { ...dossier.donnees.geolocalisation, [field]: value }
      }
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Editar Dossier</h1>
          <p className="text-slate-500 mt-1">{dossier?.reference}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/dossier/${id}`)}>
          Cancelar
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {/* Date Installation */}
          <div className="form-section">
            <h3 className="form-section-title">Fecha de Instalación</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Fecha de instalación</Label>
                <Input 
                  type="date"
                  value={dossier?.date_installation ? dossier.date_installation.split('/').reverse().join('-') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [y, m, d] = e.target.value.split('-');
                      setDossier({ ...dossier, date_installation: `${d}/${m}/${y}` });
                    } else {
                      setDossier({ ...dossier, date_installation: '' });
                    }
                  }}
                  data-testid="edit-date-installation"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {dossier?.date_installation ? `Actuelle: ${dossier.date_installation}` : 'Non définie'}
                </p>
              </div>
            </div>
          </div>

          {/* Identity Section */}
          <div className="form-section">
            <h3 className="form-section-title">Datos del Propietario</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nombre completo</Label>
                <Input 
                  value={dossier?.donnees?.identite?.nom_proprietaire || ""} 
                  onChange={(e) => updateIdentite('nom_proprietaire', e.target.value)}
                />
              </div>
              <div>
                <Label>DNI/NIE</Label>
                <Input 
                  value={dossier?.donnees?.identite?.dni || ""} 
                  onChange={(e) => updateIdentite('dni', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Dirección completa</Label>
                <Input 
                  value={dossier?.donnees?.identite?.adresse_complete || ""} 
                  onChange={(e) => updateIdentite('adresse_complete', e.target.value)}
                />
              </div>
              <div>
                <Label>Tipo de vía</Label>
                <Input 
                  value={dossier?.donnees?.identite?.type_voie || ""} 
                  onChange={(e) => updateIdentite('type_voie', e.target.value)}
                  placeholder="CL, AV, PZ..."
                />
              </div>
              <div>
                <Label>Nombre de la vía</Label>
                <Input 
                  value={dossier?.donnees?.identite?.nom_voie || ""} 
                  onChange={(e) => updateIdentite('nom_voie', e.target.value)}
                />
              </div>
              <div>
                <Label>Número</Label>
                <Input 
                  value={dossier?.donnees?.identite?.numero || ""} 
                  onChange={(e) => updateIdentite('numero', e.target.value)}
                />
              </div>
              <div>
                <Label>Escalera</Label>
                <Input 
                  value={dossier?.donnees?.identite?.escalier || ""} 
                  onChange={(e) => updateIdentite('escalier', e.target.value)}
                />
              </div>
              <div>
                <Label>Planta</Label>
                <Input 
                  value={dossier?.donnees?.identite?.etage || ""} 
                  onChange={(e) => updateIdentite('etage', e.target.value)}
                />
              </div>
              <div>
                <Label>Puerta</Label>
                <Input 
                  value={dossier?.donnees?.identite?.porte || ""} 
                  onChange={(e) => updateIdentite('porte', e.target.value)}
                />
              </div>
              <div>
                <Label>Código postal</Label>
                <Input 
                  value={dossier?.donnees?.identite?.code_postal || ""} 
                  onChange={(e) => updateIdentite('code_postal', e.target.value)}
                />
              </div>
              <div>
                <Label>Ciudad</Label>
                <Input 
                  value={dossier?.donnees?.identite?.ville || ""} 
                  onChange={(e) => updateIdentite('ville', e.target.value)}
                />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input 
                  value={dossier?.donnees?.identite?.province || ""} 
                  onChange={(e) => updateIdentite('province', e.target.value)}
                />
              </div>
              <div>
                <Label>Comunidad Autónoma</Label>
                <Input 
                  value={dossier?.donnees?.identite?.region || ""} 
                  onChange={(e) => updateIdentite('region', e.target.value)}
                  data-testid="edit-region-input"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input 
                  value={dossier?.donnees?.identite?.telephone || ""} 
                  onChange={(e) => updateIdentite('telephone', e.target.value)}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  value={dossier?.donnees?.identite?.email || ""} 
                  onChange={(e) => updateIdentite('email', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Building Section */}
          <div className="form-section">
            <h3 className="form-section-title">Datos del Edificio</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Referencia catastral</Label>
                <Input 
                  value={dossier?.donnees?.batiment?.ref_catastrale || ""} 
                  onChange={(e) => updateBatiment('ref_catastrale', e.target.value)}
                />
              </div>
              <div>
                <Label>Superficie habitable (m²)</Label>
                <Input 
                  type="number"
                  value={dossier?.donnees?.batiment?.superficie_habitable || 0} 
                  onChange={(e) => updateBatiment('superficie_habitable', parseFloat(e.target.value))}
                />
              </div>
              <div>
                <Label>Zona climática</Label>
                <Input 
                  value={dossier?.donnees?.batiment?.zone_climatique || ""} 
                  onChange={(e) => updateBatiment('zone_climatique', e.target.value)}
                />
              </div>
              <div>
                <Label>Año construcción</Label>
                <Input 
                  type="number"
                  value={dossier?.donnees?.batiment?.annee_construction || 0} 
                  onChange={(e) => updateBatiment('annee_construction', parseInt(e.target.value))}
                />
              </div>
              <div>
                <Label>Tipo de edificio</Label>
                <Input 
                  value={dossier?.donnees?.batiment?.type_batiment || ""} 
                  onChange={(e) => updateBatiment('type_batiment', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Technical Section */}
          <div className="form-section">
            <h3 className="form-section-title">Datos Técnicos</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Ui (Transmitancia inicial) W/m²K</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={dossier?.donnees?.techniques?.ui || 0} 
                  onChange={(e) => updateTechniques('ui', parseFloat(e.target.value))}
                  data-testid="edit-ui"
                />
              </div>
              <div>
                <Label>Uf (Transmitancia final) W/m²K</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={dossier?.donnees?.techniques?.uf || 0} 
                  onChange={(e) => updateTechniques('uf', parseFloat(e.target.value))}
                  data-testid="edit-uf"
                />
              </div>
              <div>
                <Label>Superficie partición / m² aislados (isolée)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={dossier?.donnees?.techniques?.superficie_partition || 0} 
                  onChange={(e) => updateTechniques('superficie_partition', parseFloat(e.target.value))}
                  data-testid="edit-superficie-partition"
                />
              </div>
              <div>
                <Label>Clasificación energética</Label>
                <Input 
                  value={dossier?.donnees?.techniques?.classification_energetique || ""} 
                  onChange={(e) => updateTechniques('classification_energetique', e.target.value)}
                  data-testid="edit-classification"
                />
              </div>
            </div>
          </div>

          {/* Surfaces Calculation Section */}
          <div className="form-section">
            <h3 className="form-section-title">Calcul des Surfaces de l'Enveloppe</h3>
            
            {/* Opaque Surfaces */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-slate-700">Cerramientos opacos (surfaces individuelles)</Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => addSurfaceItem('surfaces_opaques_list')}
                  data-testid="add-opaque-surface"
                >
                  <Plus className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {(dossier?.donnees?.techniques?.surfaces_opaques_list || []).map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-6 text-right">{idx + 1}.</span>
                    <Input 
                      type="number"
                      step="0.01"
                      value={val}
                      onChange={(e) => updateSurfaceItem('surfaces_opaques_list', idx, e.target.value)}
                      className="flex-1"
                      data-testid={`opaque-surface-${idx}`}
                    />
                    <span className="text-xs text-slate-500">m²</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeSurfaceItem('surfaces_opaques_list', idx)}
                      className="text-red-500 hover:text-red-700 p-1 h-auto"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {(dossier?.donnees?.techniques?.surfaces_opaques_list || []).length === 0 && (
                  <p className="text-xs text-slate-400 italic">Aucune surface ajoutée. Cliquez "Ajouter" ou entrez le total directement ci-dessous.</p>
                )}
              </div>
            </div>

            {/* Glass Surfaces */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-slate-700">Huecos y lucernarios (surfaces individuelles)</Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => addSurfaceItem('surfaces_vitrees_list')}
                  data-testid="add-glass-surface"
                >
                  <Plus className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {(dossier?.donnees?.techniques?.surfaces_vitrees_list || []).map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-6 text-right">{idx + 1}.</span>
                    <Input 
                      type="number"
                      step="0.01"
                      value={val}
                      onChange={(e) => updateSurfaceItem('surfaces_vitrees_list', idx, e.target.value)}
                      className="flex-1"
                      data-testid={`glass-surface-${idx}`}
                    />
                    <span className="text-xs text-slate-500">m²</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeSurfaceItem('surfaces_vitrees_list', idx)}
                      className="text-red-500 hover:text-red-700 p-1 h-auto"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {(dossier?.donnees?.techniques?.surfaces_vitrees_list || []).length === 0 && (
                  <p className="text-xs text-slate-400 italic">Aucune surface ajoutée. Cliquez "Ajouter" ou entrez le total directement ci-dessous.</p>
                )}
              </div>
            </div>

            <Separator className="my-4" />

            {/* Calculated Totals (read-only display + manual override) */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-slate-600 mb-3">Résultats calculés automatiquement</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Cerramientos opacos (total)</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      step="0.01"
                      value={dossier?.donnees?.techniques?.surface_murs || 0}
                      onChange={(e) => {
                        setDossier({
                          ...dossier,
                          donnees: {
                            ...dossier.donnees,
                            techniques: { ...dossier.donnees.techniques, surface_murs: parseFloat(e.target.value) || 0 }
                          }
                        });
                      }}
                      className="bg-white"
                      data-testid="total-opaques"
                    />
                    <span className="text-sm text-slate-500 whitespace-nowrap">m²</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Huecos y lucernarios (total)</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      step="0.01"
                      value={dossier?.donnees?.techniques?.surface_vitres || 0}
                      onChange={(e) => {
                        setDossier({
                          ...dossier,
                          donnees: {
                            ...dossier.donnees,
                            techniques: { ...dossier.donnees.techniques, surface_vitres: parseFloat(e.target.value) || 0 }
                          }
                        });
                      }}
                      className="bg-white"
                      data-testid="total-vitres"
                    />
                    <span className="text-sm text-slate-500 whitespace-nowrap">m²</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Surface totale de l'enveloppe globale</Label>
                  <div className="text-lg font-bold text-slate-900" data-testid="total-envolvente">
                    {dossier?.donnees?.techniques?.total_envolvente?.toFixed(2) || "0.00"} m²
                  </div>
                  <p className="text-xs text-slate-400">
                    = {(dossier?.donnees?.techniques?.surface_murs || 0).toFixed(2)} + {(dossier?.donnees?.techniques?.surface_vitres || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pourcentage d'enveloppe globale isolée</Label>
                  <div className="text-lg font-bold text-green-600" data-testid="pourcentage-isole">
                    {dossier?.donnees?.techniques?.pourcentage_isole?.toFixed(2) || "0.00"}%
                  </div>
                  <p className="text-xs text-slate-400">
                    = ({dossier?.donnees?.techniques?.superficie_partition || 0} x 100) / {(dossier?.donnees?.techniques?.total_envolvente || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Geolocation Section */}
          <div className="form-section">
            <h3 className="form-section-title">Geolocalización UTM</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Huso</Label>
                <Input 
                  value={dossier?.donnees?.geolocalisation?.huso || ""} 
                  onChange={(e) => updateGeolocalisation('huso', e.target.value)}
                />
              </div>
              <div>
                <Label>UTM X</Label>
                <Input 
                  type="number"
                  value={dossier?.donnees?.geolocalisation?.utm_x || 0} 
                  onChange={(e) => updateGeolocalisation('utm_x', parseFloat(e.target.value.replace(',', '.')))}
                />
              </div>
              <div>
                <Label>UTM Y</Label>
                <Input 
                  type="number"
                  value={dossier?.donnees?.geolocalisation?.utm_y || 0} 
                  onChange={(e) => updateGeolocalisation('utm_y', parseFloat(e.target.value.replace(',', '.')))}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h3 className="form-section-title">Notas</h3>
            <Textarea
              value={dossier?.notes || ""}
              onChange={(e) => setDossier({ ...dossier, notes: e.target.value })}
              rows={4}
            />
          </div>

          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate(`/dossier/${id}`)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="flex-1 btn-animate"
              data-testid="save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditDossierV2;
